import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { SpinnerVisibilityService } from 'ng-http-loader';
import { CategoryManagementService } from '../services/category-management.service';
import { AdminCategory, AdminSubcategory } from '../model/category-management-dto';
import { CategoryDialogComponent } from './category-dialog/category-dialog.component';
import { SubcategoryDialogComponent } from './subcategory-dialog/subcategory-dialog.component';

@Component({
  selector: 'app-admin-categories',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatExpansionModule,
    MatSlideToggleModule,
    MatTableModule,
    MatTooltipModule,
    MatSnackBarModule
  ],
  templateUrl: './admin-categories.component.html',
  styleUrl: './admin-categories.component.scss'
})
export class AdminCategoriesComponent implements OnInit {

  categories: AdminCategory[] = [];
  displayedColumns: string[] = ['icon', 'name', 'status', 'actions'];

  constructor(
    private service: CategoryManagementService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private spinner: SpinnerVisibilityService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadCategories();
  }

  loadCategories(): void {
    this.spinner.show();
    this.service.getCategories().subscribe({
      next: (response) => {
        this.categories = response.data || [];
        this.spinner.hide();
      },
      error: (err) => {
        this.spinner.hide();
        console.error(err);
        this.showMessage('Error cargando las categorías');
      }
    });
  }

  openCategoryDialog(category?: AdminCategory): void {
    const dialogRef = this.dialog.open(CategoryDialogComponent, {
      width: '520px',
      disableClose: true,
      data: { category }
    });
    dialogRef.afterClosed().subscribe(saved => {
      if (saved) {
        this.showMessage(category ? 'Categoría actualizada' : 'Categoría creada');
        this.loadCategories();
      }
    });
  }

  openSubcategoryDialog(category: AdminCategory, subcategory?: AdminSubcategory): void {
    const dialogRef = this.dialog.open(SubcategoryDialogComponent, {
      width: '520px',
      disableClose: true,
      data: { category, subcategory, categories: this.categories }
    });
    dialogRef.afterClosed().subscribe(saved => {
      if (saved) {
        this.showMessage(subcategory ? 'Subcategoría actualizada' : 'Subcategoría creada');
        this.loadCategories();
      }
    });
  }

  toggleCategory(category: AdminCategory, active: boolean): void {
    this.spinner.show();
    this.service.updateCategory(category.categoryId!, { active }).subscribe({
      next: () => {
        category.active = active;
        this.spinner.hide();
        this.showMessage(active ? 'Categoría habilitada' : 'Categoría deshabilitada');
      },
      error: (err) => {
        this.spinner.hide();
        console.error(err);
        this.showMessage('Error actualizando la categoría');
        this.loadCategories();
      }
    });
  }

  toggleSubcategory(subcategory: AdminSubcategory, active: boolean): void {
    this.spinner.show();
    this.service.updateSubcategory(subcategory.subcategoryId!, { active }).subscribe({
      next: () => {
        subcategory.active = active;
        this.spinner.hide();
        this.showMessage(active ? 'Subcategoría habilitada' : 'Subcategoría deshabilitada');
      },
      error: (err) => {
        this.spinner.hide();
        console.error(err);
        this.showMessage('Error actualizando la subcategoría');
        this.loadCategories();
      }
    });
  }

  openFormBuilder(subcategory: AdminSubcategory): void {
    this.router.navigate(['/admin/categories/form', subcategory.subcategoryId]);
  }

  backgroundStyle(category: AdminCategory): string {
    const color = category.backgroundColor || 'CCCCCC';
    return color.startsWith('#') ? color : `#${color}`;
  }

  private showMessage(message: string): void {
    this.snackBar.open(message, 'Cerrar', { duration: 3500 });
  }
}
